/**
 * Utilitários para manipulação de mapas no módulo de fossas sépticas
 */

import L from 'leaflet';

/**
 * Cria um ícone personalizado para marcadores no mapa
 * @param {string} status - Status da propriedade
 * @param {string} type - Tipo de marcador ('propriedade', 'residencia' ou 'fossa')
 * @returns {L.DivIcon} Ícone personalizado do Leaflet
 */
export const createCustomIcon = (status, type = 'propriedade') => {
  let color;
  let size = 24;
  let iconHtml;
  
  // Determina a cor com base no status
  switch (status) {
    case 'Mapeada':
      color = '#28a745'; // Verde
      break;
    case 'Não Mapeada':
      color = '#dc3545'; // Vermelho
      break;
    case 'Em Análise':
      color = '#ffc107'; // Amarelo
      break;
    default:
      color = '#6c757d'; // Cinza
  }
  
  // Cria ícones específicos para cada tipo
  switch (type) {
    case 'propriedade':
      // Ícone de fazenda/propriedade rural
      iconHtml = `
        <div style="
          position: relative;
          width: ${size}px;
          height: ${size}px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <!-- Cerca da propriedade -->
          <div style="
            position: absolute;
            bottom: 2px;
            left: 2px;
            right: 2px;
            height: 3px;
            background: #8B4513;
            border-radius: 1px;
          "></div>
          
          <!-- Postes da cerca -->
          <div style="
            position: absolute;
            bottom: 2px;
            left: 4px;
            width: 2px;
            height: 8px;
            background: #654321;
          "></div>
          <div style="
            position: absolute;
            bottom: 2px;
            right: 4px;
            width: 2px;
            height: 8px;
            background: #654321;
          "></div>
          
          <!-- Casa principal -->
          <div style="
            position: absolute;
            top: 4px;
            left: 50%;
            transform: translateX(-50%);
            width: 12px;
            height: 8px;
            background: ${color};
            border: 1px solid white;
            border-radius: 2px;
          "></div>
          
          <!-- Telhado -->
          <div style="
            position: absolute;
            top: 2px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 7px solid transparent;
            border-right: 7px solid transparent;
            border-bottom: 4px solid #8B4513;
          "></div>
          
          <!-- Indicador de status -->
          <div style="
            position: absolute;
            bottom: 0px;
            right: 0px;
            width: 6px;
            height: 6px;
            background: ${color};
            border: 1px solid white;
            border-radius: 50%;
            box-shadow: 0 0 2px rgba(0,0,0,0.5);
          "></div>
        </div>
      `;
      break;
      
    case 'residencia':
      // Ícone de casa/residência
      iconHtml = `
        <div style="
          position: relative;
          width: ${size}px;
          height: ${size}px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <!-- Base da casa -->
          <div style="
            position: absolute;
            bottom: 4px;
            left: 50%;
            transform: translateX(-50%);
            width: 14px;
            height: 10px;
            background: ${color};
            border: 1px solid white;
            border-radius: 1px;
          "></div>
          
          <!-- Telhado -->
          <div style="
            position: absolute;
            top: 3px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-bottom: 6px solid #8B4513;
          "></div>
          
          <!-- Porta -->
          <div style="
            position: absolute;
            bottom: 4px;
            left: 50%;
            transform: translateX(-50%);
            width: 3px;
            height: 6px;
            background: #654321;
            border-radius: 1px 1px 0 0;
          "></div>
          
          <!-- Janela -->
          <div style="
            position: absolute;
            bottom: 8px;
            right: 7px;
            width: 3px;
            height: 3px;
            background: #87CEEB;
            border: 1px solid white;
          "></div>
          
          <!-- Indicador de status -->
          <div style="
            position: absolute;
            bottom: 0px;
            right: 0px;
            width: 5px;
            height: 5px;
            background: ${color};
            border: 1px solid white;
            border-radius: 50%;
            box-shadow: 0 0 2px rgba(0,0,0,0.5);
          "></div>
        </div>
      `;
      break;
      
    case 'fossa':
      // Ícone de fossa séptica
      iconHtml = `
        <div style="
          position: relative;
          width: ${size}px;
          height: ${size}px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <!-- Tampa da fossa (circular) -->
          <div style="
            position: absolute;
            top: 6px;
            left: 50%;
            transform: translateX(-50%);
            width: 14px;
            height: 14px;
            background: #696969;
            border: 2px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          "></div>
          
          <!-- Alça da tampa -->
          <div style="
            position: absolute;
            top: 12px;
            left: 50%;
            transform: translateX(-50%);
            width: 4px;
            height: 2px;
            background: #2F4F4F;
            border-radius: 1px;
          "></div>
          
          <!-- Indicador de funcionamento -->
          <div style="
            position: absolute;
            top: 8px;
            right: 6px;
            width: 3px;
            height: 3px;
            background: #28a745;
            border: 1px solid white;
            border-radius: 50%;
            box-shadow: 0 0 2px rgba(0,0,0,0.5);
          "></div>
          
          <!-- Tubulação de entrada -->
          <div style="
            position: absolute;
            top: 10px;
            left: 2px;
            width: 6px;
            height: 2px;
            background: #8B4513;
            border-radius: 1px;
          "></div>
          
          <!-- Tubulação de saída -->
          <div style="
            position: absolute;
            top: 14px;
            right: 2px;
            width: 6px;
            height: 2px;
            background: #8B4513;
            border-radius: 1px;
          "></div>
        </div>
      `;
      break;
      
    default:
      // Ícone padrão (círculo simples)
      iconHtml = `
        <div style="
          background-color: ${color}; 
          width: 16px; 
          height: 16px; 
          border-radius: 50%; 
          border: 2px solid white; 
          box-shadow: 0 0 4px rgba(0,0,0,0.5);
        "></div>
      `;
  }
  
  // Cria o ícone personalizado
  return L.divIcon({
    className: `${type}-marker`,
    html: iconHtml,
    iconSize: [size, size],
    iconAnchor: [size / 2, size - 2],
    popupAnchor: [0, -size + 2]
  });
};

/**
 * Calcula o centro de um conjunto de coordenadas
 * @param {Array} coordinates - Array de coordenadas [lat, lng]
 * @returns {Array} Coordenadas do centro [lat, lng]
 */
export const calculateCenter = (coordinates) => {
  if (!coordinates || coordinates.length === 0) {
    // Coordenadas padrão para Conceição do Castelo - ES
    return [-20.359292, -41.247282];
  }
  
  let totalLat = 0;
  let totalLng = 0;
  
  coordinates.forEach(coord => {
    totalLat += coord[0];
    totalLng += coord[1];
  });
  
  return [totalLat / coordinates.length, totalLng / coordinates.length];
};

/**
 * Calcula o zoom adequado para exibir todas as coordenadas
 * @param {Array} coordinates - Array de coordenadas [lat, lng]
 * @param {L.Map} map - Instância do mapa Leaflet
 * @returns {number} Nível de zoom
 */
export const calculateZoom = (coordinates, map) => {
  if (!coordinates || coordinates.length <= 1 || !map) {
    return 13; // Zoom padrão (mesmo usado nos outros módulos)
  }
  
  // Cria um bounds com todas as coordenadas
  const bounds = L.latLngBounds(coordinates);
  
  // Calcula o zoom necessário para exibir todos os pontos
  map.fitBounds(bounds);
  
  return map.getZoom();
};

/**
 * Formata um GeoJSON a partir de um array de propriedades
 * @param {Array} propriedades - Array de objetos de propriedades
 * @returns {Object} Objeto GeoJSON
 */
export const formatGeoJSON = (propriedades) => {
  if (!propriedades || propriedades.length === 0) {
    return {
      type: 'FeatureCollection',
      features: []
    };
  }
  
  const features = propriedades.map(prop => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [prop.longitude, prop.latitude]
    },
    properties: {
      id: prop.id,
      nome: prop.nome,
      status: prop.status,
      tipo: 'propriedade'
    }
  }));
  
  return {
    type: 'FeatureCollection',
    features
  };
};

/**
 * Formata um GeoJSON a partir de um array de fossas sépticas
 * @param {Array} fossas - Array de objetos de fossas sépticas
 * @returns {Object} Objeto GeoJSON
 */
export const formatFossasGeoJSON = (fossas) => {
  if (!fossas || fossas.length === 0) {
    return {
      type: 'FeatureCollection',
      features: []
    };
  }
  
  const features = fossas.map(fossa => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [fossa.longitude, fossa.latitude]
    },
    properties: {
      id: fossa.id,
      propriedade_id: fossa.propriedade_id,
      tipo: 'fossa',
      tipo_fossa: fossa.tipo
    }
  }));
  
  return {
    type: 'FeatureCollection',
    features
  };
};

/**
 * Calcula a distância entre dois pontos em metros
 * @param {Array} coord1 - Coordenadas do ponto 1 [lat, lng]
 * @param {Array} coord2 - Coordenadas do ponto 2 [lat, lng]
 * @returns {number} Distância em metros
 */
export const calculateDistance = (coord1, coord2) => {
  if (!coord1 || !coord2) return 0;
  
  const lat1 = coord1[0];
  const lon1 = coord1[1];
  const lat2 = coord2[0];
  const lon2 = coord2[1];
  
  const R = 6371e3; // Raio da Terra em metros
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;
  
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return R * c; // Distância em metros
};